/**
 * 元音源の波形表示 + 連打開始位置マーカー（インクタンク風の canvas）。
 *
 * - クリック / ドラッグで連打開始位置を指定（Shift 押しながらで微調整）
 * - 連打区間（開始位置 + 連打長）をインクピンクでハイライト
 * - 試聴中は再生ヘッドを白線で表示
 * - 未読み込み時はドロップゾーンとして機能する
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

import { RENDA_COLORS, RENDA_FONT, RENDA_PANEL } from "../palette";
import { computePeaks, formatSec } from "../utils/audioLoad";

const HEIGHT = 120;

const Tank = styled.div<{ $interactive: boolean }>`
  position: relative;
  width: 100%;
  height: ${HEIGHT}px;
  border-radius: 12px;
  border: 3px solid ${RENDA_PANEL.outline};
  background: linear-gradient(
    180deg,
    #232945 0%,
    ${RENDA_PANEL.displayBg} 100%
  );
  overflow: hidden;
  cursor: ${({ $interactive }) => ($interactive ? "ew-resize" : "copy")};
  touch-action: none;
`;

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
`;

const EmptyHint = styled.div`
  position: absolute;
  inset: 10px;
  border: 2px dashed rgba(238, 247, 255, 0.35);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 8px;
  font-family: ${RENDA_FONT};
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.08em;
  color: rgba(238, 247, 255, 0.6);
  pointer-events: none;
`;

export interface WaveformDisplayProps {
  buffer: AudioBuffer | null;
  /** 連打開始位置（元音源内の秒） */
  startSec: number;
  /** 連打フレーズの長さ（秒） */
  phraseLenSec: number;
  /** 合成タイムライン上の再生位置（秒）。null で非表示 */
  playheadSec: number | null;
  onChangeStartSec: (sec: number) => void;
  onDropFile: (file: File) => void;
}

const WaveformDisplay: React.FC<WaveformDisplayProps> = ({
  buffer,
  startSec,
  phraseLenSec,
  playheadSec,
  onChangeStartSec,
  onDropFile,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [width, setWidth] = useState(0);

  const draggingRef = useRef(false);
  const lastXRef = useRef(0);
  const startSecRef = useRef(startSec);
  startSecRef.current = startSec;
  const onChangeRef = useRef(onChangeStartSec);
  onChangeRef.current = onChangeStartSec;

  // コンテナ幅の追従（リサイズでピーク解像度も再計算する）
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setWidth(Math.floor(w));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const peaks = useMemo(
    () => (buffer && width >= 4 ? computePeaks(buffer, width) : null),
    [buffer, width],
  );

  // ---- 描画 ----
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || width < 4) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(HEIGHT * dpr);
    const g = canvas.getContext("2d");
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, width, HEIGHT);
    if (!buffer || !peaks) return;

    const durSec = buffer.duration;
    const midY = HEIGHT / 2;
    const amp = HEIGHT / 2 - 8;

    // センターライン
    g.fillStyle = "rgba(238, 247, 255, 0.15)";
    g.fillRect(0, midY - 0.5, width, 1);

    // 波形（インクシアン）
    g.fillStyle = RENDA_COLORS.inkCyan;
    for (let x = 0; x < peaks.min.length; x++) {
      const yTop = midY - peaks.max[x] * amp;
      const yBottom = midY - peaks.min[x] * amp;
      g.fillRect(x, yTop, 1, Math.max(1, yBottom - yTop));
    }

    // 連打区間ハイライト（インクピンク）
    const x0 = (startSec / durSec) * width;
    const x1 = (Math.min(startSec + phraseLenSec, durSec) / durSec) * width;
    g.fillStyle = "rgba(240, 50, 125, 0.3)";
    g.fillRect(x0, 0, Math.max(2, x1 - x0), HEIGHT);

    // 開始マーカー（イエロー）
    g.fillStyle = RENDA_COLORS.inkYellow;
    g.fillRect(x0 - 1, 0, 2, HEIGHT);
    g.beginPath();
    g.moveTo(x0 - 6, 0);
    g.lineTo(x0 + 6, 0);
    g.lineTo(x0, 10);
    g.closePath();
    g.fill();

    // 位置ラベル
    g.font = `800 11px ${RENDA_FONT}`;
    g.fillStyle = RENDA_COLORS.inkYellow;
    const label = formatSec(startSec);
    const labelX = Math.min(Math.max(4, x0 + 8), width - 66);
    g.fillText(label, labelX, 18);

    // 再生ヘッド（白）
    if (playheadSec !== null && playheadSec <= durSec) {
      const px = (playheadSec / durSec) * width;
      g.fillStyle = "rgba(255, 255, 255, 0.9)";
      g.fillRect(px, 0, 1.5, HEIGHT);
    }
  }, [buffer, peaks, startSec, phraseLenSec, playheadSec, width]);

  // ---- 開始位置のポインタ操作 ----
  const applyPointer = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, isInitial: boolean) => {
      const el = containerRef.current;
      const buf = buffer;
      if (!el || !buf) return;
      const rect = el.getBoundingClientRect();
      const durSec = buf.duration;
      const clamp = (sec: number) => Math.min(durSec, Math.max(0, sec));

      if (e.shiftKey) {
        // 微調整: 移動量の 1/10 だけ動かす（初回クリックではジャンプさせない）
        if (!isInitial) {
          const deltaFrac = (e.clientX - lastXRef.current) / rect.width;
          onChangeRef.current(
            clamp(startSecRef.current + deltaFrac * durSec * 0.1),
          );
        }
      } else {
        const frac = (e.clientX - rect.left) / rect.width;
        onChangeRef.current(clamp(frac * durSec));
      }
      lastXRef.current = e.clientX;
    },
    [buffer],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!buffer) return;
      e.preventDefault();
      (e.target as Element).setPointerCapture?.(e.pointerId);
      draggingRef.current = true;
      lastXRef.current = e.clientX;
      applyPointer(e, true);
    },
    [buffer, applyPointer],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return;
      applyPointer(e, false);
    },
    [applyPointer],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      draggingRef.current = false;
      (e.target as Element).releasePointerCapture?.(e.pointerId);
    },
    [],
  );

  // ---- ドラッグ & ドロップ読み込み ----
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (file) onDropFile(file);
    },
    [onDropFile],
  );

  return (
    <Tank
      ref={containerRef}
      $interactive={buffer !== null}
      role={buffer ? "slider" : undefined}
      aria-label={buffer ? "連打開始位置" : undefined}
      aria-valuemin={buffer ? 0 : undefined}
      aria-valuemax={
        buffer ? Math.round(buffer.duration * 1000) / 1000 : undefined
      }
      aria-valuenow={buffer ? Math.round(startSec * 1000) / 1000 : undefined}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <Canvas ref={canvasRef} />
      {!buffer && (
        <EmptyHint>
          オーディオファイルをここにドロップ
          <br />
          または LOAD ボタンで読み込み
        </EmptyHint>
      )}
    </Tank>
  );
};

export default WaveformDisplay;
